import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

// UI Components
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LoadingButton } from '@/components/ui/loading-button';

// Icons
import { 
  GraduationCap,
  Plus,
  Edit,
  Trash2,
  Users,
  User,
  Settings,
  BookOpen,
  MapPin,
  Calendar,
  Search,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';

// Custom hooks
import { useLevels } from '@/hooks/useLevels';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ClassManagement = ({ user }) => {
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLevel, setSelectedLevel] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
  const [selectedClass, setSelectedClass] = useState(null);
  const [classStudents, setClassStudents] = useState([]);

  // Form states
  const [classForm, setClassForm] = useState({
    name: '',
    level: '',
    description: '',
    capacity: 30,
    teacher_id: '',
    room: '',
    academic_year: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`
  });

  // Utiliser le hook personnalisé pour les niveaux
  const { levels: classLevels, loading: levelsLoading, getLevelLabel } = useLevels();

  // Ancienne liste hardcodée remplacée par l'API
  /* const classLevels = [
    // Maternelle et Primaire
    { value: 'MATERNELLE', label: 'Maternelle', category: 'Maternelle' },
    { value: 'PETITE_SECTION', label: 'Petite Section', category: 'Maternelle' },
    { value: 'GRANDE_SECTION', label: 'Grande Section', category: 'Maternelle' },
    { value: 'CP1', label: 'CP1 (1ère année)', category: 'Primaire' },
    { value: 'CP2', label: 'CP2 (2ᵉ année)', category: 'Primaire' },
    { value: 'CE1', label: 'CE1 (3ᵉ année)', category: 'Primaire' },
    { value: 'CE2', label: 'CE2 (4ᵉ année)', category: 'Primaire' },
    { value: 'CM1', label: 'CM1 (5ᵉ année)', category: 'Primaire' },
    { value: 'CM2', label: 'CM2 (6ᵉ année)', category: 'Primaire' },
    
    // Secondaire - Collège
    { value: 'ANNEE_7', label: '7ᵉ année', category: 'Collège' },
    { value: 'ANNEE_8', label: '8ᵉ année', category: 'Collège' },
    { value: 'ANNEE_9', label: '9ᵉ année', category: 'Collège' },
    { value: 'ANNEE_10', label: '10ᵉ année', category: 'Collège' },
    
    // Secondaire - 11ᵉ année spécialisée
    { value: 'ANNEE_11_SCIENCES_EXP', label: '11ᵉ année Sciences Expérimentales', category: '11ᵉ année' },
    { value: 'ANNEE_11_SCIENCES_MATH', label: '11ᵉ année Sciences Mathématiques', category: '11ᵉ année' },
    { value: 'ANNEE_11_SCIENCES_SOC', label: '11ᵉ année Sciences Sociales', category: '11ᵉ année' },
    
    // Secondaire - 12ᵉ année spécialisée
    { value: 'ANNEE_12_SCIENCES', label: '12ᵉ année Sciences', category: '12ᵉ année' },
    { value: 'ANNEE_12_SCIENCES_MATH', label: '12ᵉ année Sciences Mathématiques', category: '12ᵉ année' },
    { value: 'ANNEE_12_SCIENCES_SOC', label: '12ᵉ année Sciences Sociales', category: '12ᵉ année' },
    { value: 'ANNEE_12_SCIENCES_EXP', label: '12ᵉ année Sciences Expérimentales', category: '12ᵉ année' },
    
    // Terminale spécialisée
    { value: 'TERMINALE_SCIENCES_MATH', label: 'Terminale Sciences Mathématiques', category: 'Terminale' },
    { value: 'TERMINALE_SCIENCES_EXP', label: 'Terminale Sciences Expérimentales', category: 'Terminale' },
    { value: 'TERMINALE_SCIENCES_SOC', label: 'Terminale Sciences Sociales', category: 'Terminale' }
  ]; */

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [classesResponse, teachersResponse] = await Promise.all([
        axios.get(`${API}/classes`),
        axios.get(`${API}/users?role=TEACHER`)
      ]);

      setClasses(classesResponse.data);
      setTeachers(teachersResponse.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    try {
      // Préparer les données en convertissant "none" en null pour teacher_id
      const formData = {
        ...classForm,
        teacher_id: classForm.teacher_id === 'none' ? null : classForm.teacher_id
      };
      
      const response = await axios.post(`${API}/classes`, formData);
      setClasses(prev => [...prev, response.data]);
      toast.success('Classe créée avec succès');
      resetForm();
      setCreateDialogOpen(false);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(errorMsg);
      console.error('Error creating class:', error);
    }
  };

  const handleUpdateClass = async (e) => {
    e.preventDefault();
    if (!selectedClass) return;

    try {
      // Préparer les données en convertissant "none" en null pour teacher_id
      const formData = {
        ...classForm,
        teacher_id: classForm.teacher_id === 'none' ? null : classForm.teacher_id
      };
      
      const response = await axios.put(`${API}/classes/${selectedClass.id}`, formData);
      setClasses(prev => prev.map(cls => 
        cls.id === selectedClass.id ? response.data : cls
      ));
      toast.success('Classe mise à jour avec succès');
      resetForm();
      setEditDialogOpen(false);
      setSelectedClass(null);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la mise à jour';
      toast.error(errorMsg);
      console.error('Error updating class:', error);
    }
  };

  const handleDeleteClass = async (classId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette classe ?')) {
      return;
    }

    try {
      const response = await axios.delete(`${API}/classes/${classId}`);
      
      // Refresh classes list
      await fetchData();
      
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(errorMsg);
      console.error('Error deleting class:', error);
    }
  };

  const fetchClassStudents = async (classId) => {
    try {
      const response = await axios.get(`${API}/classes/${classId}/students`);
      setClassStudents(response.data.students);
      setSelectedClass(response.data.class_info);
      setStudentsDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors du chargement des étudiants');
      console.error('Error fetching class students:', error);
    }
  };

  const resetForm = () => {
    setClassForm({
      name: '',
      level: '',
      description: '',
      capacity: 30,
      teacher_id: 'none',
      room: '',
      academic_year: `${new Date().getFullYear()}-${new Date().getFullYear() + 1}`
    });
  };

  const openEditDialog = (schoolClass) => {
    setSelectedClass(schoolClass);
    setClassForm({
      name: schoolClass.name,
      level: schoolClass.level,
      description: schoolClass.description || '',
      capacity: schoolClass.capacity,
      teacher_id: schoolClass.teacher_id || 'none',
      room: schoolClass.room || '',
      academic_year: schoolClass.academic_year
    });
    setEditDialogOpen(true);
  };

  const getTeacherName = (teacherId) => {
    const teacher = teachers.find(t => t.id === teacherId);
    return teacher ? `${teacher.first_name} ${teacher.last_name}` : 'Non assigné';
  };

  // getLevelLabel is now provided by the useLevels hook

  const filteredClasses = classes.filter(schoolClass => {
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = schoolClass.name.toLowerCase().includes(searchLower);
    const levelMatch = !selectedLevel || selectedLevel === 'all' || schoolClass.level === selectedLevel;
    return nameMatch && levelMatch;
  });

  if (user.role !== 'ADMIN' && user.role !== 'PRINCIPAL') {
    return (
      <div className="p-6">
        <Card className="card">
          <CardContent className="pt-6">
            <div className="text-center">
              <AlertTriangle className="w-16 h-16 mx-auto text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Accès refusé</h3>
              <p className="text-gray-600">
                Vous n'avez pas les permissions nécessaires pour gérer les classes.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
            <div className="h-32 bg-gray-200 rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 fade-in">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold flex items-center">
            <GraduationCap className="w-6 h-6 mr-2 text-blue-600" />
            Gestion des Classes
          </h1>
          <p className="text-gray-600 mt-1">
            Organisez les classes et gérez les niveaux scolaires
          </p>
        </div>

        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          {user?.role === 'ADMIN' && (
            <DialogTrigger asChild>
              <Button className="btn-primary" data-testid="create-class-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle classe
              </Button>
            </DialogTrigger>
          )}
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Créer une nouvelle classe</DialogTitle>
              <DialogDescription>
                Ajoutez une nouvelle classe à votre établissement
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateClass} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nom de la classe *</Label>
                  <Input
                    id="name"
                    value={classForm.name}
                    onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                    placeholder="Ex: CP1-A, CE2-B"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="level">Niveau *</Label>
                  <Select value={classForm.level} onValueChange={(value) => setClassForm({ ...classForm, level: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Choisir le niveau" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64 overflow-y-auto">
                      {classLevels.map((level) => (
                        <SelectItem key={level.level} value={level.level}>
                          {level.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Input
                  id="description"
                  value={classForm.description}
                  onChange={(e) => setClassForm({ ...classForm, description: e.target.value })}
                  placeholder="Description de la classe"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="capacity">Capacité</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={classForm.capacity}
                    onChange={(e) => setClassForm({ ...classForm, capacity: parseInt(e.target.value) })}
                    min="1"
                    max="50"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="room">Salle</Label>
                  <Input
                    id="room"
                    value={classForm.room}
                    onChange={(e) => setClassForm({ ...classForm, room: e.target.value })}
                    placeholder="Ex: A101, B205"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="teacher_id">Enseignant principal</Label>
                <Select value={classForm.teacher_id} onValueChange={(value) => setClassForm({ ...classForm, teacher_id: value })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un enseignant" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun enseignant assigné</SelectItem>
                    {teachers.map((teacher) => (
                      <SelectItem key={teacher.id} value={teacher.id}>
                        {teacher.first_name} {teacher.last_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="academic_year">Année scolaire</Label>
                <Input
                  id="academic_year"
                  value={classForm.academic_year}
                  onChange={(e) => setClassForm({ ...classForm, academic_year: e.target.value })}
                  placeholder="2024-2025"
                  required
                />
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button type="button" variant="outline" onClick={() => {
                  resetForm();
                  setCreateDialogOpen(false);
                }}>
                  Annuler
                </Button>
                <LoadingButton 
                  type="submit" 
                  className="btn-primary"
                  loading={loading}
                  loadingText="Création de la classe..."
                >
                  Créer la classe
                </LoadingButton>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <GraduationCap className="w-4 h-4 mr-2 text-blue-600" />
              Total Classes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">{classes.length}</div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <Users className="w-4 h-4 mr-2 text-green-600" />
              Classes Actives
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {classes.filter(c => c.is_active).length}
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <User className="w-4 h-4 mr-2 text-purple-600" />
              Avec Enseignant
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-purple-600">
              {classes.filter(c => c.teacher_id).length}
            </div>
          </CardContent>
        </Card>

        <Card className="card">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center">
              <BookOpen className="w-4 h-4 mr-2 text-orange-600" />
              Capacité Totale
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {classes.reduce((sum, c) => sum + c.capacity, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex space-x-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Rechercher une classe..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        <Select value={selectedLevel} onValueChange={setSelectedLevel}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Filtrer par niveau" />
          </SelectTrigger>
          <SelectContent className="max-h-64 overflow-y-auto">
            <SelectItem value="all">Tous les niveaux</SelectItem>
            {classLevels.map((level) => (
              <SelectItem key={level.level} value={level.level}>
                {level.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Classes List */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredClasses.map((schoolClass) => (
          <Card key={schoolClass.id} className="card">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="flex items-center">
                    <GraduationCap className="w-5 h-5 mr-2 text-blue-600" />
                    {schoolClass.name}
                  </CardTitle>
                  <CardDescription>
                    {getLevelLabel(schoolClass.level)}
                  </CardDescription>
                </div>
                <div className="flex space-x-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEditDialog(schoolClass)}
                  >
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDeleteClass(schoolClass.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            
            <CardContent>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span>Enseignant:</span>
                  <Badge variant="outline">
                    {getTeacherName(schoolClass.teacher_id)}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span>Capacité:</span>
                  <Badge variant="outline">
                    {schoolClass.capacity} places
                  </Badge>
                </div>

                {schoolClass.room && (
                  <div className="flex items-center justify-between">
                    <span>Salle:</span>
                    <Badge variant="outline">
                      <MapPin className="w-3 h-3 mr-1" />
                      {schoolClass.room}
                    </Badge>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <span>Année:</span>
                  <Badge variant="outline">
                    <Calendar className="w-3 h-3 mr-1" />
                    {schoolClass.academic_year}
                  </Badge>
                </div>

                <div className="flex items-center justify-between">
                  <span>Statut:</span>
                  <Badge className={schoolClass.is_active ? 'badge-success' : 'badge-secondary'}>
                    {schoolClass.is_active ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Active
                      </>
                    ) : (
                      <>
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        Inactive
                      </>
                    )}
                  </Badge>
                </div>

                {schoolClass.description && (
                  <p className="text-gray-600 text-xs mt-2">
                    {schoolClass.description}
                  </p>
                )}
              </div>

              <Button
                className="w-full mt-4 btn-secondary"
                size="sm"
                onClick={() => fetchClassStudents(schoolClass.id)}
              >
                <Users className="w-4 h-4 mr-2" />
                Voir les étudiants
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredClasses.length === 0 && (
        <div className="text-center py-12">
          <GraduationCap className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune classe trouvée</h3>
          <p className="text-gray-600 mb-4">
            {searchTerm || selectedLevel ? 'Aucune classe ne correspond aux critères de recherche.' : 'Aucune classe créée.'}
          </p>
          {!searchTerm && !selectedLevel && (
            <Button onClick={() => setCreateDialogOpen(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Créer la première classe
            </Button>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier la classe</DialogTitle>
            <DialogDescription>
              Modifiez les informations de la classe
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateClass} className="space-y-4">
            {/* Same form fields as create dialog */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_name">Nom de la classe *</Label>
                <Input
                  id="edit_name"
                  value={classForm.name}
                  onChange={(e) => setClassForm({ ...classForm, name: e.target.value })}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_level">Niveau *</Label>
                <Select value={classForm.level} onValueChange={(value) => setClassForm({ ...classForm, level: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {classLevels.map((level) => (
                      <SelectItem key={level.level} value={level.level}>
                        {level.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_description">Description</Label>
              <Input
                id="edit_description"
                value={classForm.description}
                onChange={(e) => setClassForm({ ...classForm, description: e.target.value })}
                placeholder="Description de la classe"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="edit_capacity">Capacité</Label>
                <Input
                  id="edit_capacity"
                  type="number"
                  value={classForm.capacity}
                  onChange={(e) => setClassForm({ ...classForm, capacity: parseInt(e.target.value) })}
                  min="1"
                  max="50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit_room">Salle</Label>
                <Input
                  id="edit_room"
                  value={classForm.room}
                  onChange={(e) => setClassForm({ ...classForm, room: e.target.value })}
                  placeholder="Ex: A101, B205"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_teacher_id">Enseignant principal</Label>
              <Select value={classForm.teacher_id} onValueChange={(value) => setClassForm({ ...classForm, teacher_id: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un enseignant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun enseignant assigné</SelectItem>
                  {teachers.map((teacher) => (
                    <SelectItem key={teacher.id} value={teacher.id}>
                      {teacher.first_name} {teacher.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_academic_year">Année scolaire</Label>
              <Input
                id="edit_academic_year"
                value={classForm.academic_year}
                onChange={(e) => setClassForm({ ...classForm, academic_year: e.target.value })}
                placeholder="2024-2025"
                required
              />
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => {
                resetForm();
                setEditDialogOpen(false);
                setSelectedClass(null);
              }}>
                Annuler
              </Button>
              <LoadingButton 
                type="submit" 
                className="btn-primary"
                loading={loading}
                loadingText="Mise à jour..."
              >
                Mettre à jour
              </LoadingButton>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Students List Dialog */}
      <Dialog open={studentsDialogOpen} onOpenChange={setStudentsDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>
              Étudiants de la classe {selectedClass?.name}
            </DialogTitle>
            <DialogDescription>
              Liste des {classStudents.length} étudiant(s) inscrit(s) dans cette classe
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {classStudents.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto text-gray-400 mb-3" />
                <p className="text-gray-600">Aucun étudiant inscrit dans cette classe</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {classStudents.map((student, index) => (
                  <div 
                    key={student.id} 
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {student.user_info?.first_name} {student.user_info?.last_name}
                        </p>
                        <p className="text-sm text-gray-600">
                          {student.user_info?.email}
                        </p>
                        {student.student_number && (
                          <p className="text-xs text-gray-500">
                            Matricule: {student.student_number}
                          </p>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                      #{index + 1}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </div>
          
          <div className="mt-4 flex justify-end">
            <Button onClick={() => setStudentsDialogOpen(false)} variant="outline">
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClassManagement;